<template>
  <form class="h-full flex flex-col justify-between" @submit.prevent="checkForm">
    <div>
      <p class="font-bold mb-5">Confirm your business details</p>
      <DrInput
        ref="policyholder"
        :placeholder="businessName"
        :error="errors.businessName"
        data-label="Insured name"
        class="mb-3"
        disabled
      />
      <Transition name="fade" mode="out-in">
        <DrInput
          v-if="showTradingName"
          ref="tradingName"
          v-model="tradingName"
          :error="errors.tradingName"
          data-label="Trading name"
          class="my-6"
          placeholder="Trading name"
        />
        <div v-else>
          <div
            class="text-blue-500 text-sm inline-block cursor-pointer mb-3"
            tabindex="0"
            @click="showTradingName = !showTradingName"
            @keyup.enter="showTradingName = !showTradingName"
          >
            Add trading name
          </div>
        </div>
      </Transition>
      <div v-if="hasEmployersLiability">
        <DrInput
          v-model="ern"
          class="mb-3"
          :error="errors.ern"
          :disabled="ernExempt"
          data-label="Employers Reference Number (ERN)"
          :placeholder="ernExempt ? 'Not required' : '123/AB12345'"
        />
        <DrInputCheckbox
          v-model="ernExempt"
          class="mb-6"
          data-cy="ern-exempt"
          data-label="We're exempt from having an ERN or will provide it within 30 days."
        />
      </div>
      <div class="text-sm mb-1 inline-block text-grey-575">Your premises</div>
      <div class="bg-white shadow">
        <div v-if="premises.length" class="relative text-2xs text-grey-575">
          <div v-for="address in premises" :key="address.line1">
            <div class="flex justify-between items-center border-b border-grey-150 py-3 pr-3 pl-4">
              <div>
                <div class="address-line">{{ address.line1 }}</div>
                <div v-if="address.line2" class="address-line">{{ address.line2 }}</div>
                <div v-if="address.line3" class="address-line">{{ address.line3 }}</div>
                <div class="address-line">{{ address.city }}</div>
                <div class="address-line">{{ address.postcode }}</div>
                <div class="address-line">{{ address.country }}</div>
              </div>
              <DrIcon
                data-cy="premises-delete"
                svg="close"
                width="20"
                height="20"
                class="text-blue-500 cursor-pointer hover:text-blue-600"
                @click.native="deletePremises({ quoteId: $store.state.quoteId, premisesId: address._id })"
              />
            </div>
          </div>
        </div>
        <div class="p-3 text-center bg-white" :class="{ 'border border-red-650 rounded': errors.premises }">
          <RouterLink
            data-cy="add-premises-btn"
            to="/checkout/address"
            class="btn btn-sm btn-outline btn-blue inline-block text-2xs"
          >
            {{ premises.length ? 'Add another premises' : 'Add premises' }}
          </RouterLink>
        </div>
      </div>
      <div v-if="errors.premises" class="error pt-1 flex items-center">
        <DrIcon svg="close-solid" class="mr-1" />
        <small>{{ errors.premises }}</small>
      </div>
      <p class="text-2xs mt-3 mb-6 leading-normal text-grey-575">Add each of the premises you operate from.</p>
    </div>
    <div>
      <DrButton
        :loading="loading"
        class="w-full btn-blue"
        data-cy="btn-submit-business-details"
        @click.prevent="checkForm"
      >
        <template v-if="canSetEffectiveDate">Select start date</template>
        <template v-else>Set up payment</template>
      </DrButton>
    </div>
  </form>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

export default {
  data () {
    return {
      loading: false,
      showTradingName: false,
      errors: {},
    }
  },
  computed: {
    ...mapGetters({
      premises: 'premises/all',
      getProducts: 'getProducts',
    }),
    firstName: {
      get () {
        return this.$store.getters.getFirstName
      },
      set (value) {
        this.$store.commit('updateFirstName', value)
      },
    },
    lastName: {
      get () {
        return this.$store.getters.getLastName
      },
      set (value) {
        this.$store.commit('updateLastName', value)
      },
    },
    businessName: {
      get () {
        return this.$store.getters.getBusinessName
      },
      set (value) {
        this.$store.commit('updateBusinessName', value)
      },
    },
    ern: {
      get () {
        return this.$store.getters.getErn
      },
      set (value) {
        this.$store.commit('updateErn', value)
      },
    },
    ernExempt: {
      get () {
        return this.$store.getters.getErnExempt
      },
      set (value) {
        this.$store.commit('updateErnExempt', value)
      },
    },
    employeeCount: {
      get () {
        return this.$store.getters.getEmployeeCount
      },
    },
    hasEmployersLiability () {
      const products = this.$store.getters.getProducts

      const product = products.filter(item => item.key === 'CommercialCombined' || item.key === 'AvivaCommercialCombined')

      if (product.length) {
        const rate = product[0].rates.filter(item => item.key === 'CommercialCombined\\GeneralLiability' || item.key === 'AvivaCommercialCombined\\GeneralLiability')

        if (rate.length && rate[0].ratingInputs.employersLiabilityLimit > 0) return true

        return product[0].rates.filter(item => item.key === 'CommercialCombined\\EmployersLiability' || item.key === 'AvivaCommercialCombined\\EmployersLiability').length > 0
      }

      return false
    },
    tradingName: {
      get () {
        return this.$store.getters.getTradingName
      },
      set (value) {
        this.$store.commit('updateTradingName', value)
      },
    },
    convertedToAccountRef: {
      get () {
        return this.$store.getters.getConvertedToAccountRef
      },
      set (value) {
        this.$store.commit('updateConvertedToAccountRef', value)
      },
    },
    sanctionsSearchRecord: {
      get () {
        return this.$store.getters.getSanctionsSearchRecord
      },
      set (value) {
        this.$store.commit(`updateSanctionsSearchRecord`, value)
      },
    },
    canSetEffectiveDate () {
      // Popup offices skip the effective date page
      return !this.getProducts
        .filter(item => item.key === 'CommercialCombined')
        .indexOf(({ key }) => key === 'CommercialCombined\\AppearHere')
    },
  },
  created () {
    if (this.tradingName) this.showTradingName = true
  },
  async mounted () {
    // ERN Exempt if single employee or no employers liability
    if (this.employeeCount < 2 || !this.hasEmployersLiability) this.ernExempt = true

    await this.fetchPremises({ quoteId: this.$store.state.quoteId })

    this.$refs.policyholder.$refs.input.focus()
  },
  methods: {
    ...mapActions({
      fetchPremises: 'premises/fetchPremises',
      deletePremises: 'premises/deletePremises',
    }),
    checkForm () {
      this.loading = true
      this.errors = {}
      if (!this.businessName || (!this.ern && !this.ernExempt) || !this.premises.length) {
        if (!this.businessName) this.errors.businessName = `You must provide the insured name`
        if (!this.ern && !this.ernExempt) this.errors.ern = `You must provide an ERN or tick the box below`
        if (!this.premises.length) this.errors.premises = 'You must provide at least one address'
        this.loading = false

        return
      }

      this.$router.push('/checkout/policy-start-date').catch(() => {})
    },
  },
}
</script>
